import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, Loader2, Clock, ShieldCheck } from 'lucide-react';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { Text } from '@/Components/ui/typography';
import { useAuth } from '../../Hooks/useAuth';
import { getApiUrl } from '@/config/api';
import { getColor } from '@/config/theme';
import { forgetTeamLogin } from '../../Context/CompanyAccessContext';

function LoginForm() {
  // Arriving from a just-accepted invite: the address is known and worth confirming.
  const { state: arrival } = useLocation();
  const [email, setEmail] = useState(arrival?.email || '');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState('error'); // 'error' | 'pending'
  const [loading, setLoading] = useState(false);

  // T2-3: MFA second step
  const [mfaTicket, setMfaTicket] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  // Shown after an auto-logout when the session/token expired.
  const [sessionExpired] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('session') === 'expired'; }
    catch { return false; }
  });

  const navigate = useNavigate();
  const { login } = useAuth();

  const persistAdmin = (data) => {
    forgetTeamLogin();
    const persist = (store) => {
      store.setItem('admin_email', data.admin_email);
      store.setItem('admin_id', data.admin_id);
      store.setItem('is_super_admin', data.is_super_admin ? '1' : '0');
      if (data.admin_token) store.setItem('admin_token', data.admin_token);
      if (data.admin_role || data.role) store.setItem('admin_role', data.admin_role || data.role);
    };
    persist(sessionStorage);
    persist(localStorage);
    localStorage.setItem('admin_full_name', data.full_name);
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (loading) return;
    setErrorMessage('');
    setLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/mfa/verify-login'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_ticket: mfaTicket, code: mfaCode }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Invalid code');
      persistAdmin(data);
      navigate('/company-admin-home');
    } catch (error) {
      setErrorMessage(error.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setErrorMessage('');
    setErrorType('error');
    setLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('password', password);
      const response = await fetch(getApiUrl('/api/hirewithus-login'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        // 403 = account blocked (pending approval or rejected) — show specific message
        if (response.status === 403) {
          setErrorType('pending');
          setErrorMessage(data.message || 'Access denied.');
          return;
        }
        throw new Error(data.message || 'Login failed');
      }

      // T2-3: MFA challenge — show the code step instead of completing login.
      if (data.mfa_required) {
        setMfaTicket(data.mfa_ticket);
        return;
      }

      // Admin Login
      if (data.role === 'admin') {
        persistAdmin(data);
        navigate('/company-admin-home');
        return;
      }

      // Employer / HR Login
      await login(data);
      // First-time login (profile not set up yet) → redirect to company setup
      if (data.is_profile_setup === false) {
        navigate('/hirewithus/company/companysetup/');
      } else {
        navigate('/hirewithus/overview');
      }

    } catch (error) {
      setErrorMessage(error.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  // T2-3: MFA code step
  if (mfaTicket) {
    return (
      <form className="flex flex-col gap-5" onSubmit={handleMfaVerify}>
        <div className="flex flex-col items-center gap-2 text-center">
          <ShieldCheck className="w-8 h-8 text-primary" />
          <Text size="sm" className="text-muted-foreground">
            Enter the 6-digit code from your authenticator app.
          </Text>
        </div>
        <Input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="123456"
          className="h-11 text-center tracking-[0.5em] text-lg"
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
          disabled={loading}
          autoFocus
          required
        />
        {errorMessage && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
            <Text size="sm" variant="destructive">{errorMessage}</Text>
          </div>
        )}
        <Button
          type="submit"
          disabled={loading || mfaCode.length < 6}
          className="w-full h-11 font-semibold flex items-center justify-center gap-2"
          style={{ backgroundColor: getColor('primary'), color: getColor('primaryForeground'), borderColor: getColor('primary') }}
        >
          {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />Verifying...</>) : 'Verify'}
        </Button>
        <button
          type="button"
          onClick={() => { setMfaTicket(''); setMfaCode(''); setErrorMessage(''); }}
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          Back to login
        </button>
      </form>
    );
  }

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
      <div className="space-y-4">
        <div className="relative group">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within:text-primary" />
          <Input
            type="email"
            placeholder="Enter your email"
            className="pl-10 h-10 transition-all"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
          />
        </div>

        <div className="space-y-2">
          <div className="relative group">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within:text-primary" />
            <Input
              type="password"
              placeholder="Enter your password"
              className="pl-10 h-10 transition-all"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="flex items-center justify-end">
            <Link
              to="/hirewithus/forgot-password"
              className="text-sm text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </div>
      </div>

      {arrival?.notice && !errorMessage && (
        <div className="rounded-md bg-green-50 border border-green-200 p-3">
          <Text size="sm" className="text-green-800">{arrival.notice}</Text>
        </div>
      )}

      {sessionExpired && !errorMessage && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
          <Clock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <Text size="sm" className="text-amber-800">Your session expired. Please sign in again.</Text>
        </div>
      )}

      {errorMessage && (
        errorType === 'pending' ? (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
            <Clock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <Text size="sm" className="text-amber-800">{errorMessage}</Text>
          </div>
        ) : (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
            <Text size="sm" variant="destructive">{errorMessage}</Text>
          </div>
        )
      )}

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2"
        style={{
          backgroundColor: getColor('primary'),
          color: getColor('primaryForeground'),
          borderColor: getColor('primary')
        }}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Signing In...
          </>
        ) : (
          "Sign In"
        )}
      </Button>
    </form>
  );
}

export default LoginForm;
